'use client';
import React, { useState } from 'react';
import { HorizontalSplit } from '@/components/HorizontalSplit';
import { Input } from '@/components/Input';
import { PageInfo } from '@/app/(auth)/signup/components/PageInfo';
import { ISendEmailRecoveryResult } from '@/interfaces/okta/ISendEmailRecoveryResult';
import { useMutation } from '@tanstack/react-query';
import { LoadingButton } from '@/components/LoadingButton';
import { useRouter } from 'next/navigation';
import { ArrowRight } from '@/components/icons/ArrowRight';

interface IRequestEmailClientProps {
  isPassport: boolean;
  myAccountUrl: string;
  requestPasswordResetAction(email: string, type: string): Promise<ISendEmailRecoveryResult>;
}

export default function RequestEmailClient({
  isPassport,
  myAccountUrl,
  requestPasswordResetAction,
}: IRequestEmailClientProps) {
  const [email, setEmail] = useState('');

  const router = useRouter();

  const submitEmailMutation = useMutation({
    mutationKey: ['submitEmail'],
    mutationFn: ([email]: [string]) => requestPasswordResetAction(email, 'EMAIL'),
  });

  return (
    <HorizontalSplit>
      <PageInfo headline="Forgot Password" />

      <div>
        {submitEmailMutation.isSuccess ? (
          <div className="tw:flex tw:flex-col tw:justify-between tw:gap-2 tw:w-full">
            <h2 className="tw:text-2xl tw:leading-tight tw:lg:mb-4 tw:font-medium"> Recovery Email Sent </h2>
            <p>
              {' '}
              If this email is associated with an account, you will receive an email with details on how to
              reset your password.
            </p>
            {!isPassport ? (
              <a
                className={
                  'tw:bg-white tw:text-primary tw:border-2 tw:border-primary tw:w-full tw:rounded-full tw:py-4 tw:mt-4 tw:group-invalid:pointer-events-none tw:group-invalid:opacity-30 tw:flex tw:justify-center tw:items-center tw:font-medium tw:text-lg tw:transition tw:duration-150 tw:ease-in-out tw:cursor-pointer'
                }
                id={'return-to-homepage'}
                href={myAccountUrl}>
                Return to Homepage
              </a>
            ) : null}
          </div>
        ) : (
          <form
            id="email_submit_form"
            className="tw:group"
            onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              submitEmailMutation.mutate([email]);
            }}
            noValidate>
            <div className="tw:flex tw:flex-col tw:justify-between tw:gap-2 tw:w-full">
              <Input
                id={'email-input'}
                name={'email'}
                label={'Email'}
                placeholder={'example@example.com'}
                type={'email'}
                value={email}
                onChange={value => setEmail(value.trim())}
                required
              />
              <div className="tw:flex tw:justify-end tw:flex-col-reverse tw:sm:flex-row tw:flex-1 tw:gap-4 tw:min-h-[4em] tw:mt-4">
                <LoadingButton
                  id="previous"
                  size="md"
                  className="tw:bg-white tw:text-primary tw:border-2 tw:border-primary tw:w-full tw:lg:w-1/4 tw:xl:w-1/5 tw:2xl:w-1/6 tw:rounded-full tw:py-4 tw:flex tw:justify-center tw:items-center tw:font-medium tw:text-lg"
                  onClick={e => {
                    e.preventDefault();
                    router.push('/login');
                  }}
                  type="button"
                  label="Back"
                  variant="secondary"
                />
                <LoadingButton
                  size="md"
                  variant="primary"
                  id={'submitEmail'}
                  type="submit"
                  className={`tw:w-full tw:lg:w-1/3 tw:xl:w-1/4 tw:2xl:w-1/5 tw:border-2 tw:rounded-full tw:py-4 tw:flex tw:justify-center tw:items-center tw:font-medium tw:text-lg tw:transition-opacity tw:duration-150 tw:ease-in-out ${
                    email
                      ? 'tw:bg-primary tw:text-white tw:border-primary'
                      : 'tw:bg-white tw:text-gray-400 tw:border-gray-300'
                  }`}
                  isLoading={submitEmailMutation.isPending}
                  disabled={!email}
                  label={
                    <span className="tw:flex tw:items-center tw:gap-2">
                      <ArrowRight fill={`${!!email ? 'white' : '#A0AEC0'}`} />
                      Submit
                    </span>
                  }
                />
              </div>
              {submitEmailMutation.isError ? (
                <div className="tw:text-red-600 tw:text-sm">
                  {submitEmailMutation.error?.message || 'An error occurred'}
                </div>
              ) : null}
            </div>
          </form>
        )}
      </div>
    </HorizontalSplit>
  );
}
